'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { authApi, tokenStore, subscribeSessionLost, ApiError } from '@/lib/api';
import { API_BASE_URL } from '@/lib/api-base-url';
import type { AuthUser } from '@/lib/api';

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** true = BE está disponível e respondendo */
  isOnline: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (email: string, password: string, name?: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  /** OAuth Google — persiste o access token e atualiza o contexto antes do redirect */
  setSession: (accessToken: string, user: AuthUser) => void;
  /** Recarrega dados do usuário autenticado (ex.: após salvar perfil) */
  refreshUser: () => Promise<AuthUser | null>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ao montar: tenta restaurar sessão do token salvo
  useEffect(() => {
    const restore = async () => {
      // Callback OAuth define a sessão via setSession — evita corrida com restore/me
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/auth/callback')) {
        setIsLoading(false);
        return;
      }

      const storedUser = tokenStore.getUser() as AuthUser | null;
      const token = tokenStore.getAccess();

      if (storedUser && token) {
        // Libera layout e marca online cedo: /auctions é público e pode carregar em paralelo ao /auth/me
        setUser(storedUser);
        setIsLoading(false);
        setIsOnline(true);

        // Valida o token em background
        try {
          const fresh = await authApi.me();
          setUser(fresh);
          setIsOnline(true);
        } catch {
          tokenStore.clear();
          setUser(null);
          setIsOnline(false);
        }
      } else {
        // Sem token: libera imediatamente para redirecionar ao login
        setIsLoading(false);

        // Verifica saúde do BE em background (não bloqueia)
        fetch(`${API_BASE_URL}/health`)
          .then(() => setIsOnline(true))
          .catch(() => setIsOnline(false));
      }
    };

    restore();
  }, []);

  useEffect(
    () =>
      subscribeSessionLost(() => {
        setUser(null);
        setIsOnline(false);
      }),
    []
  );

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      const { user: u } = await authApi.login(email, password);
      setUser(u);
      setIsOnline(true);
      return u;
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Erro ao conectar com o servidor';
      setError(msg);
      throw e;
    }
  }, []);

  const register = useCallback(async (email: string, password: string, name?: string) => {
    setError(null);
    try {
      const { user: u } = await authApi.register(email, password, name);
      setUser(u);
      setIsOnline(true);
      return u;
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Erro ao conectar com o servidor';
      setError(msg);
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    router.replace('/');
  }, [router]);

  const setSession = useCallback((accessToken: string, sessionUser: AuthUser) => {
    tokenStore.set(accessToken, sessionUser);
    setUser(sessionUser);
    setIsLoading(false);
    setIsOnline(true);
    setError(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!tokenStore.getAccess()) return null;
    try {
      const fresh = await authApi.me();
      tokenStore.setUser(fresh);
      setUser(fresh);
      return fresh;
    } catch {
      tokenStore.clear();
      setUser(null);
      setIsOnline(false);
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        isOnline,
        login,
        register,
        logout,
        setSession,
        refreshUser,
        error,
        clearError: () => setError(null),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}
